import { useAuth } from "./AuthContext";

/**
 * Custom hook for claim-based permission checks.
 * 
 * Claims available:
 * - "Create Master", "Edit Master", "Delete Master", "Read Master", "Cancel Master"
 * - "Create Transaction", "Edit Transaction", "Delete Transaction", "Read Transaction", "Cancel Transaction"
 * - "Create Accounts", "Edit Accounts", "Delete Accounts", "Read Accounts", "Cancel Accounts"
 * - "Create Reports", "Edit Reports", "Delete Reports", "Read Reports", "Cancel Reports"
 * - "Create Administration", "Edit Administration", "Delete Administration", "Read Administration", "Cancel Administration"
 */
export const usePermissions = () => {
  const { permissions, isSuperAdmin, roleName } = useAuth();

  /**
   * Check if user has a specific permission claim
   */
  const hasPermission = (claim) => {
    if (isSuperAdmin || roleName === "SuperAdmin") return true;
    return permissions.includes(claim);
  };

  /**
   * Check if user has ANY of the given permission claims
   */
  const hasAnyPermission = (claims) => {
    if (isSuperAdmin || roleName === "SuperAdmin") return true;
    return claims.some((claim) => permissions.includes(claim));
  };

  /**
   * Check if user has ALL of the given permission claims
   */
  const hasAllPermissions = (claims) => {
    if (isSuperAdmin || roleName === "SuperAdmin") return true;
    return claims.every((claim) => permissions.includes(claim));
  };

  // Pre-computed section-level permissions
  const canReadMaster = hasPermission("Read Master");
  const canCreateMaster = hasPermission("Create Master");
  const canEditMaster = hasPermission("Edit Master");
  const canDeleteMaster = hasPermission("Delete Master");
  const canCancelMaster = hasPermission("Cancel Master");

  const canReadTransaction = hasPermission("Read Transaction");
  const canCreateTransaction = hasPermission("Create Transaction");
  const canEditTransaction = hasPermission("Edit Transaction");
  const canDeleteTransaction = hasPermission("Delete Transaction");
  const canCancelTransaction = hasPermission("Cancel Transaction");

  const canReadAccounts = hasPermission("Read Accounts");
  const canCreateAccounts = hasPermission("Create Accounts");
  const canEditAccounts = hasPermission("Edit Accounts");
  const canDeleteAccounts = hasPermission("Delete Accounts");
  const canCancelAccounts = hasPermission("Cancel Accounts");

  const canReadReports = hasPermission("Read Reports");
  const canCreateReports = hasPermission("Create Reports");
  const canEditReports = hasPermission("Edit Reports");
  const canDeleteReports = hasPermission("Delete Reports");
  const canCancelReports = hasPermission("Cancel Reports");

  const canReadAdministration = hasPermission("Read Administration");
  const canCreateAdministration = hasPermission("Create Administration");
  const canEditAdministration = hasPermission("Edit Administration");
  const canDeleteAdministration = hasPermission("Delete Administration");
  const canCancelAdministration = hasPermission("Cancel Administration");

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    // Master
    canReadMaster,
    canCreateMaster,
    canEditMaster,
    canDeleteMaster,
    canCancelMaster,
    // Transaction
    canReadTransaction,
    canCreateTransaction,
    canEditTransaction,
    canDeleteTransaction,
    canCancelTransaction,
    // Accounts
    canReadAccounts,
    canCreateAccounts,
    canEditAccounts,
    canDeleteAccounts,
    canCancelAccounts,
    // Reports
    canReadReports,
    canCreateReports,
    canEditReports,
    canDeleteReports,
    canCancelReports,
    // Administration
    canReadAdministration,
    canCreateAdministration,
    canEditAdministration,
    canDeleteAdministration,
    canCancelAdministration,
  };
};

export default usePermissions;
